/**
 * OpenAPI 3.x → wrapper-spec converter.
 *
 * Walks `paths.<path>.<method>` operations and emits one tool per
 * operation. Pulls schemas from `parameters` + `requestBody`, derives
 * realm declarations from `securitySchemes`, and assigns `auth_realm`
 * per operation based on the operation's `security` block (or top-level
 * `security` if absent).
 *
 * Governance fields the wrapper spec needs but OpenAPI doesn't carry
 * (`data_classification`, `side_effect_level`) get safe defaults the
 * user reviews after import.
 *
 * Accepts JSON text. YAML support is a thin shim — we do not pull a
 * heavy YAML lib; if the input starts with `{` we parse as JSON,
 * otherwise we attempt a tiny YAML→JSON via a permissive parser. Most
 * teams export OpenAPI as JSON or paste with a `>`-converter so this
 * covers the common case; for complex YAML, ask the user to paste JSON.
 */

interface ImportOptions {
  defaultOwningWgId?: string;
  suggestedSlug?: string;
}

export interface ImportedRealm {
  token_url?: string;
  client_id_env?: string;
  client_secret_env?: string;
  scope?: string;
}

export interface ImportedTool {
  name?: string;
  description?: string;
  input_schema?: unknown;
  data_classification?: string;
  side_effect_level?: string;
  auth_realm?: string;
  request?: {
    method?: string;
    url_template?: string;
    headers?: Record<string, string>;
    query?: Record<string, string>;
    body?: unknown;
  };
}

export interface ImportedSpec {
  slug: string;
  name: string;
  description?: string;
  owning_wg_id?: string;
  auth_realms: Record<string, ImportedRealm>;
  tools: ImportedTool[];
}

interface OAS3 {
  openapi?: string;
  info?: { title?: string; description?: string };
  servers?: { url: string }[];
  components?: {
    securitySchemes?: Record<string, OASSecurityScheme>;
  };
  security?: { [name: string]: string[] }[];
  paths?: Record<string, OASPathItem>;
}
interface OASPathItem {
  parameters?: OASParameter[];
  [method: string]: unknown;
}
interface OASOperation {
  operationId?: string;
  summary?: string;
  description?: string;
  tags?: string[];
  parameters?: OASParameter[];
  requestBody?: { content?: Record<string, { schema?: unknown }>; required?: boolean };
  responses?: Record<string, unknown>;
  security?: { [name: string]: string[] }[];
}
interface OASParameter {
  name: string;
  in: 'query' | 'path' | 'header' | 'cookie';
  required?: boolean;
  schema?: { type?: string; format?: string; enum?: unknown[]; description?: string };
  description?: string;
}
interface OASSecurityScheme {
  type: 'http' | 'apiKey' | 'oauth2' | 'openIdConnect' | string;
  scheme?: string; // for http
  in?: 'header' | 'query' | 'cookie'; // for apiKey
  name?: string; // for apiKey
  flows?: {
    clientCredentials?: { tokenUrl?: string; scopes?: Record<string, string> };
    authorizationCode?: { tokenUrl?: string; scopes?: Record<string, string> };
  };
}

export function openApiToWrapperSpec(text: string, opts: ImportOptions = {}): ImportedSpec {
  if (!text.trim()) throw new Error('empty input');
  const doc = parseOpenApi(text);
  if (!doc.openapi || !doc.openapi.startsWith('3')) {
    throw new Error(`expected OpenAPI 3.x (got "${doc.openapi ?? 'missing'}")`);
  }
  if (!doc.paths || Object.keys(doc.paths).length === 0) {
    throw new Error('no paths in OpenAPI doc');
  }

  const baseUrl = (doc.servers?.[0]?.url ?? '').replace(/\/+$/, '');
  const title = doc.info?.title ?? 'imported-api';
  const slug = opts.suggestedSlug ?? slugify(title);

  // Realms from securitySchemes.
  const auth_realms: Record<string, ImportedRealm> = {};
  for (const [name, scheme] of Object.entries(doc.components?.securitySchemes ?? {})) {
    if (scheme.type === 'oauth2') {
      const flow = scheme.flows?.clientCredentials ?? scheme.flows?.authorizationCode;
      auth_realms[name] = {
        token_url: flow?.tokenUrl,
      };
    } else {
      // apiKey / http-basic / http-bearer all get a realm placeholder;
      // user picks the actual mode + fills creds on the Credentials page.
      auth_realms[name] = {};
    }
  }

  const tools: ImportedTool[] = [];
  for (const [path, item] of Object.entries(doc.paths)) {
    if (!item || typeof item !== 'object') continue;
    const pathParams = (item.parameters ?? []) as OASParameter[];
    for (const m of ['get', 'post', 'put', 'patch', 'delete'] as const) {
      const op = (item as Record<string, unknown>)[m] as OASOperation | undefined;
      if (!op || typeof op !== 'object') continue;

      const opParams = [...pathParams, ...(op.parameters ?? [])];
      const queryParams = opParams.filter((p) => p.in === 'query');
      const pathTokens = opParams.filter((p) => p.in === 'path');
      const headerParams = opParams.filter((p) => p.in === 'header');

      // Build URL template: {id} → {{args.id}}
      let urlTemplate = `${baseUrl}${path}`;
      for (const p of pathTokens) {
        urlTemplate = urlTemplate.replace(`{${p.name}}`, `{{args.${p.name}}}`);
      }

      // Input schema: combine path + query params + requestBody.
      const properties: Record<string, unknown> = {};
      const required: string[] = [];
      for (const p of [...pathTokens, ...queryParams]) {
        properties[p.name] = {
          type: p.schema?.type ?? 'string',
          ...(p.schema?.format ? { format: p.schema.format } : {}),
          ...(p.description ? { description: p.description } : {}),
          ...(p.schema?.enum ? { enum: p.schema.enum } : {}),
        };
        if (p.required) required.push(p.name);
      }
      let body: unknown = undefined;
      if (op.requestBody) {
        const json = op.requestBody.content?.['application/json'];
        if (json?.schema) {
          // Surface body fields as input args too: a flat copy.
          const bodySchema = json.schema as {
            properties?: Record<string, unknown>;
            required?: string[];
          };
          if (bodySchema.properties) {
            for (const [k, v] of Object.entries(bodySchema.properties)) {
              properties[k] = v;
            }
            for (const r of bodySchema.required ?? []) required.push(r);
          }
          // Body template: object of args interpolations.
          body = templateFromBodyProperties(bodySchema.properties ?? {});
        }
      }

      // Headers from header params (with arg interpolation).
      const headers: Record<string, string> = {};
      for (const h of headerParams) {
        headers[h.name] = `{{args.${h.name}}}`;
      }
      // Query from query params (with arg interpolation).
      const query: Record<string, string> = {};
      for (const q of queryParams) {
        query[q.name] = `{{args.${q.name}}}`;
      }

      // Auth realm: prefer operation-level security, then document-level.
      const sec = op.security ?? doc.security ?? [];
      const realmName = sec.length > 0 ? Object.keys(sec[0]!)[0] : undefined;

      const inputSchema: Record<string, unknown> = { type: 'object', properties };
      if (required.length > 0) inputSchema.required = [...new Set(required)];

      tools.push({
        name: sanitizeToolName(op.operationId ?? `${m}_${path}`),
        description: (op.summary ?? op.description ?? '').trim() || `${m.toUpperCase()} ${path}`,
        input_schema: inputSchema,
        data_classification: 'internal',
        side_effect_level: m === 'get' ? 'read_only' : m === 'delete' ? 'destructive' : 'write_low',
        ...(realmName ? { auth_realm: realmName } : {}),
        request: {
          method: m.toUpperCase(),
          url_template: urlTemplate,
          ...(Object.keys(headers).length > 0 ? { headers } : {}),
          ...(Object.keys(query).length > 0 ? { query } : {}),
          ...(body !== undefined ? { body } : {}),
        },
      });
    }
  }

  if (tools.length === 0) throw new Error('no operations found in paths');

  return {
    slug,
    name: title,
    description: doc.info?.description,
    owning_wg_id: opts.defaultOwningWgId,
    auth_realms,
    tools,
  };
}

function templateFromBodyProperties(props: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of Object.keys(props)) {
    out[k] = `{{args.${k}}}`;
  }
  return out;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

function sanitizeToolName(s: string): string {
  return (
    s
      .replace(/[^a-zA-Z0-9_]/g, '_')
      .replace(/^_+/, '')
      .replace(/_+/g, '_')
      .slice(0, 60) || 'tool'
  );
}

/** Parse OpenAPI input. JSON preferred; tiny YAML fallback for the simple case. */
function parseOpenApi(text: string): OAS3 {
  const trimmed = text.trim();
  if (trimmed.startsWith('{')) {
    return JSON.parse(trimmed) as OAS3;
  }
  // Minimal YAML→JSON via the user's browser (no extra dep).
  // We only support the common shape: 2-space indent, no anchors, no flow
  // sequences. If parsing fails, surface the error verbatim.
  return tinyYaml(trimmed) as OAS3;
}

/**
 * Tiny YAML parser — handles the OpenAPI common subset:
 *   key: value
 *   key:
 *     nested: value
 *   - listitem
 *   "key": "value"
 *
 * Doesn't handle: anchors, aliases, flow style ({a: b}), multi-line strings,
 * tags, complex keys. Throws on anything ambiguous.
 */
function tinyYaml(input: string): unknown {
  const lines = input
    .split(/\r?\n/)
    .map((l) => ({ raw: l, trim: l.replace(/\s+#.*$/, '').trimEnd() }))
    .filter((l) => l.trim.length > 0 && !l.trim.trimStart().startsWith('#'));

  let i = 0;
  function indentOf(s: string): number {
    let n = 0;
    while (n < s.length && s[n] === ' ') n++;
    return n;
  }
  function parseValue(raw: string): unknown {
    const v = raw.trim();
    if (v === '' || v === '~' || v === 'null') return null;
    if (v === 'true') return true;
    if (v === 'false') return false;
    if (/^-?\d+(\.\d+)?$/.test(v)) return Number(v);
    const sq = /^'(.*)'$/.exec(v);
    if (sq) return sq[1];
    const dq = /^"(.*)"$/.exec(v);
    if (dq) return dq[1];
    return v;
  }
  function parseBlock(parentIndent: number): unknown {
    let result: unknown = null;
    let isList: boolean | null = null;
    while (i < lines.length) {
      const line = lines[i]!;
      const ind = indentOf(line.raw);
      if (ind <= parentIndent) break;
      const content = line.raw.slice(ind);
      if (content.startsWith('- ') || content === '-') {
        if (isList === false) throw new Error(`mixed list/map at line ${i + 1}`);
        isList = true;
        if (!Array.isArray(result)) result = [];
        const inline = content.slice(2).trim();
        if (inline === '') {
          i++;
          (result as unknown[]).push(parseBlock(ind));
        } else if (inline.includes(': ')) {
          // List item that's a map: rewind so the map parses with the same
          // indent + a virtual prefix.
          (result as unknown[]).push(parseInlineKv(inline, ind));
          i++;
        } else {
          (result as unknown[]).push(parseValue(inline));
          i++;
        }
        continue;
      }
      // map line
      if (isList === true) throw new Error(`mixed list/map at line ${i + 1}`);
      isList = false;
      if (typeof result !== 'object' || result === null || Array.isArray(result)) result = {};
      const colon = content.indexOf(':');
      if (colon < 0) throw new Error(`expected "key:" at line ${i + 1}`);
      const key = content
        .slice(0, colon)
        .trim()
        .replace(/^["']|["']$/g, '');
      const after = content.slice(colon + 1).trim();
      i++;
      if (after === '') {
        (result as Record<string, unknown>)[key] = parseBlock(ind);
      } else {
        (result as Record<string, unknown>)[key] = parseValue(after);
      }
    }
    return result;
  }
  function parseInlineKv(content: string, indent: number): unknown {
    const colon = content.indexOf(':');
    const key = content.slice(0, colon).trim();
    const rest = content.slice(colon + 1).trim();
    const obj: Record<string, unknown> = {};
    obj[key] = rest ? parseValue(rest) : parseBlock(indent + 2);
    return obj;
  }
  return parseBlock(-1);
}
